import httpClient from './httpClient';
import {
  AITaskSuggestionRequest,
  AITaskSuggestionResponse,
  AIFeedbackRequest,
  AIFeedbackResponse,
  AIQuotaResponse,
  AITaskBreakdownRequest,
  AITaskBreakdownResponse,
} from '../types/AI';
import { AIDecisionContext, DecisionRequest, DecisionResponse, Decision, ProposedChanges } from '../types/Decision';
import { DecisionTemplate } from '../types/DecisionTemplate';
import { DecisionGenerationJob } from '../types/DecisionGenerationJob';

/**
 * Generate AI-powered task description suggestion
 */
export const generateTaskDescriptionSuggestion = async (
  request: AITaskSuggestionRequest
): Promise<AITaskSuggestionResponse> => {
  const response = await httpClient.post<AITaskSuggestionResponse>('/v1/ai/suggestions/task-description', request);
  return response.data;
};

export type AIIntent = 'chat' | 'update' | 'both';

export interface AIStreamCallbacks {
  onIntent?: (intent: AIIntent, reasoning: string) => void;
  onChatChunk?: (chunk: string) => void;
  onDescriptionChunk?: (chunk: string) => void;
  onChatDone?: () => void;
  onComplete: (metadata: {
    intent?: AIIntent;
    contentId: number;
    tokensUsed: number;
    cost: number;
    cached: boolean;
  }) => void;
  onError: (error: string) => void;
}

/**
 * Generate AI-powered task description suggestion with streaming
 * Supports three-way action flow: chat, update, or both
 */
export const generateTaskDescriptionSuggestionStreaming = async (
  request: AITaskSuggestionRequest,
  callbacks: AIStreamCallbacks,
  signal?: AbortSignal
): Promise<void> => {
  const baseURL = httpClient.defaults.baseURL || '';
  const url = `${baseURL}/v1/ai/suggestions/task-description/stream`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to start streaming' }));
    throw new Error(errorData.error || 'Failed to start streaming');
  }

  const reader = response.body?.getReader();
  const decoder = new TextDecoder();

  if (!reader) {
    throw new Error('No response body');
  }

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.substring(6));

            switch (data.type) {
              case 'intent':
                callbacks.onIntent?.(data.data.intent, data.data.reasoning);
                break;

              case 'chat':
                callbacks.onChatChunk?.(data.data);
                break;

              case 'description':
                callbacks.onDescriptionChunk?.(data.data);
                break;

              case 'content': // Backward compatibility
                callbacks.onDescriptionChunk?.(data.data);
                break;

              case 'chat_done':
                callbacks.onChatDone?.();
                break;

              case 'done':
                callbacks.onComplete({
                  intent: data.data.intent,
                  contentId: data.data.contentId,
                  tokensUsed: data.data.tokensUsed,
                  cost: data.data.cost,
                  cached: data.data.cached,
                });
                break;

              case 'error':
                callbacks.onError(data.data.message);
                break;
            }
          } catch (parseError) {
            console.error('Failed to parse SSE data:', parseError);
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
};

/**
 * Submit feedback on AI-generated content
 */
export const submitAIFeedback = async (request: AIFeedbackRequest): Promise<AIFeedbackResponse> => {
  const response = await httpClient.post<AIFeedbackResponse>('/v1/ai/feedback', request);
  return response.data;
};

/**
 * Get AI quota information for an organization
 */
export const getAIQuota = async (organizationId: number): Promise<AIQuotaResponse> => {
  const response = await httpClient.get<AIQuotaResponse>('/v1/ai/quota', {
    params: { organizationId },
  });
  return response.data;
};

/**
 * Generate AI-powered task breakdown into subtasks
 */
export const generateTaskBreakdown = async (request: AITaskBreakdownRequest): Promise<AITaskBreakdownResponse> => {
  const response = await httpClient.post<AITaskBreakdownResponse>('/v1/ai/suggestions/task-breakdown', request);
  return response.data;
};

/**
 * Get AI-generated decision context for a task
 */
export const getDecisionContext = async (taskId: number): Promise<AIDecisionContext> => {
  const response = await httpClient.post<AIDecisionContext>('/v1/ai/decision-context', { taskId });
  return response.data;
};

/**
 * Log a decision made in the Decision Inbox
 */
export const logDecision = async (request: DecisionRequest): Promise<DecisionResponse> => {
  const response = await httpClient.post<DecisionResponse>('/v1/ai/decisions', request);
  return response.data;
};

/**
 * Trigger morning briefing (server-to-server, called by cron)
 */
export const triggerMorningBriefing = async (
  workspaceId?: number,
  cronSecret?: string
): Promise<{ briefingsSent: number; errors: string[] }> => {
  const response = await httpClient.post<{ briefingsSent: number; errors: string[] }>(
    '/v1/ai/morning-briefing',
    workspaceId ? { workspaceId } : {},
    { headers: cronSecret ? { 'X-Cron-Secret': cronSecret } : {} }
  );
  return response.data;
};

export type AIBreakdownIntent = 'chat' | 'breakdown' | 'both';

export interface AIBreakdownStreamCallbacks {
  onMode?: (mode: AIBreakdownIntent, reasoning: string) => void;
  onChatChunk?: (chunk: string) => void;
  onContentChunk?: (chunk: string) => void;
  onChatDone?: () => void;
  onComplete: (metadata: {
    mode?: AIBreakdownIntent;
    contentId: number;
    tokensUsed: number;
    cost: number;
    cached: boolean;
    breakdown?: any[];
    reasoning?: string;
  }) => void;
  onError: (error: string) => void;
}

/**
 * Generate AI-powered task breakdown with streaming
 * Supports three-way action flow: chat, breakdown, or both
 */
export const generateTaskBreakdownStreaming = async (
  request: AITaskBreakdownRequest,
  callbacks: AIBreakdownStreamCallbacks,
  signal?: AbortSignal
): Promise<void> => {
  const baseURL = httpClient.defaults.baseURL || '';
  const url = `${baseURL}/v1/ai/suggestions/task-breakdown/stream`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to start streaming' }));
    throw new Error(errorData.error || 'Failed to start streaming');
  }

  const reader = response.body?.getReader();

  if (!reader) {
    throw new Error('No response body');
  }

  // The final 'done' event carries the whole breakdown, which is routinely larger than one
  // network read, so events must be reassembled across reads rather than parsed per chunk.
  let receivedTerminalEvent = false;

  await readSSEDataLines(reader, (payload) => {
    let data;
    try {
      data = JSON.parse(payload);
    } catch (parseError) {
      console.error('Failed to parse SSE data:', parseError);
      return;
    }

    switch (data.type) {
      case 'mode':
        callbacks.onMode?.(data.data.mode, data.data.reasoning);
        break;

      case 'chat':
        callbacks.onChatChunk?.(data.data);
        break;

      case 'content':
        callbacks.onContentChunk?.(data.data);
        break;

      case 'chat_done':
        callbacks.onChatDone?.();
        break;

      case 'done':
        receivedTerminalEvent = true;
        callbacks.onComplete({
          mode: data.data.mode,
          contentId: data.data.contentId,
          tokensUsed: data.data.tokensUsed,
          cost: data.data.cost,
          cached: data.data.cached,
          breakdown: data.data.breakdown,
          reasoning: data.data.reasoning,
        });
        break;

      case 'error':
        receivedTerminalEvent = true;
        callbacks.onError(data.data?.message || 'Failed to generate breakdown');
        break;
    }
  });

  if (!receivedTerminalEvent) {
    throw new Error('The breakdown response ended unexpectedly. Please try again.');
  }
};

/**
 * Reads a Server-Sent Events body and calls onData with each complete `data:` payload.
 * Lines are buffered across reads, so an event split over several network chunks is
 * delivered once, intact.
 */
const readSSEDataLines = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onData: (payload: string) => void
): Promise<void> => {
  const decoder = new TextDecoder();
  let buffer = '';

  const flushLines = (final: boolean) => {
    const lines = buffer.split('\n');
    // Keep the trailing partial line for the next read, unless the stream has ended
    buffer = final ? '' : (lines.pop() ?? '');
    for (const rawLine of lines) {
      const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
      if (line.startsWith('data: ')) {
        onData(line.substring(6));
      }
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      flushLines(false);
    }
    buffer += decoder.decode();
    flushLines(true);
  } finally {
    reader.releaseLock();
  }
};

// =====================================================================
// Decisions (v1 redo)
// =====================================================================

export const fetchDecisions = async (
  workspaceId: number,
  status: 'pending' | 'accepted' | 'rejected' | 'skipped' | 'all' = 'pending'
): Promise<{ decisions: Decision[] }> => {
  const response = await httpClient.get<{ decisions: Decision[] }>(
    `/v1/ai/workspaces/${workspaceId}/decisions?status=${status}`
  );
  return response.data;
};

export const acceptDecision = async (
  id: number,
  edits?: ProposedChanges
): Promise<{ ok: true; decided: ProposedChanges }> => {
  const response = await httpClient.post<{ ok: true; decided: ProposedChanges }>(
    `/v1/ai/decisions/${id}/accept`,
    { edits }
  );
  return response.data;
};

export const rejectDecision = async (id: number, reason?: string): Promise<{ ok: true }> => {
  const response = await httpClient.post<{ ok: true }>(`/v1/ai/decisions/${id}/reject`, { reason });
  return response.data;
};

export const skipDecision = async (id: number): Promise<{ ok: true }> => {
  const response = await httpClient.post<{ ok: true }>(`/v1/ai/decisions/${id}/skip`);
  return response.data;
};

export const rejectAllDecisions = async (workspaceId: number): Promise<{ rejected: number }> => {
  const response = await httpClient.post<{ rejected: number }>(
    `/v1/ai/workspaces/${workspaceId}/decisions/reject-all`
  );
  return response.data;
};

export const clearDecidedDecisions = async (workspaceId: number): Promise<{ cleared: number }> => {
  const response = await httpClient.post<{ cleared: number }>(
    `/v1/ai/workspaces/${workspaceId}/decisions/clear-decided`
  );
  return response.data;
};

// Generation
export const generateDecisions = async (
  workspaceId: number,
  body: { count: 10 | 20 | 30; instructions: string; templateId?: number }
): Promise<{ jobId: number; status: string }> => {
  const response = await httpClient.post<{ jobId: number; status: string }>(
    `/v1/ai/workspaces/${workspaceId}/decisions/generate`,
    body
  );
  return response.data;
};

export const fetchJob = async (jobId: number): Promise<{ job: DecisionGenerationJob }> => {
  const response = await httpClient.get<{ job: DecisionGenerationJob }>(`/v1/ai/decisions/jobs/${jobId}`);
  return response.data;
};

export const fetchMyActiveJobs = async (): Promise<{ jobs: DecisionGenerationJob[] }> => {
  const response = await httpClient.get<{ jobs: DecisionGenerationJob[] }>(`/v1/ai/decisions/jobs/mine/active`);
  return response.data;
};

export const clarifyJob = async (
  jobId: number,
  message: string
): Promise<{ jobId: number; status: string }> => {
  const response = await httpClient.post<{ jobId: number; status: string }>(
    `/v1/ai/decisions/jobs/${jobId}/clarify`,
    { message }
  );
  return response.data;
};

export const cancelJob = async (jobId: number): Promise<{ ok: true }> => {
  const response = await httpClient.post<{ ok: true }>(`/v1/ai/decisions/jobs/${jobId}/cancel`);
  return response.data;
};

// Templates
export const fetchTemplates = async (): Promise<{ templates: DecisionTemplate[] }> => {
  const response = await httpClient.get<{ templates: DecisionTemplate[] }>(`/v1/ai/decision-templates`);
  return response.data;
};

export const createTemplate = async (body: {
  title: string;
  instructions: string;
}): Promise<{ template: DecisionTemplate }> => {
  const response = await httpClient.post<{ template: DecisionTemplate }>(`/v1/ai/decision-templates`, body);
  return response.data;
};

export const updateTemplate = async (
  id: number,
  body: { title?: string; instructions?: string }
): Promise<{ template: DecisionTemplate }> => {
  const response = await httpClient.put<{ template: DecisionTemplate }>(
    `/v1/ai/decision-templates/${id}`,
    body
  );
  return response.data;
};

export const deleteTemplate = async (id: number): Promise<void> => {
  await httpClient.delete(`/v1/ai/decision-templates/${id}`);
};

export const reorderTemplates = async (ids: number[]): Promise<{ ok: true }> => {
  const response = await httpClient.put<{ ok: true }>(`/v1/ai/decision-templates/reorder`, { ids });
  return response.data;
};
